import { cloneDeep, orderBy, sortBy, uniqBy } from 'lodash-es';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { IBookingSubset } from 'external-services/booking-availability-service/types';
import { bookingStatusType, BookingType, IAffectedBooking, IBooking, IDiarySchedule, ISchedule } from 'shared-types/index';

export enum LIST_TYPE {
  LIST_TYPE_WAITLIST = 'waitlist',
  LIST_TYPE_BOOKINGS = 'booked',
  LIST_TYPE_SEATED = 'seated',
  LIST_TYPE_CANCELLED = 'cancelled',
  LIST_TYPE_FINISHED = 'finished',
  LIST_TYPE_ERRORS = 'errors',
  LIST_TYPE_AFFECTED = 'affected',

  /**
   * standby list has its own column for diary mobile
   */
  LIST_TYPE_STANDBY_LIST = 'standbyList',
}

// export interface CategoryKey = 'aba'
export interface ICategorisedBookingList {
  [key: string]: IBooking[];
}

const BOOKING_TYPE_DESC = 'bookingType';
const TIME = 'time';
const BOOKED_AT = 'bookedAt';
const LAST_NAME = 'lastName';
const SBWL_SortOrder = [BOOKING_TYPE_DESC, BOOKED_AT, LAST_NAME];
const Booking_SortOrder = [TIME, LAST_NAME];

class BookingListFilterService {
  public getBookingListByListType(
    listType: LIST_TYPE,
    bookingsList: ICategorisedBookingList,
    affectedBookings: IBooking[]
  ): IBooking[] {
    let currentList;
    if (listType === LIST_TYPE.LIST_TYPE_WAITLIST) {
      // As per the discussion with Jem - The bookings should be ordered based on the booking time. Also the bookings should be grouped together
      currentList = orderBy(bookingsList[listType], SBWL_SortOrder, ['desc', 'asc', 'asc']);
    } else if (
      listType !== LIST_TYPE.LIST_TYPE_AFFECTED &&
      listType !== LIST_TYPE.LIST_TYPE_ERRORS
    ) {
      // As per the discussion with Jem - The bookings should be ordered based on the booking time. Also the bookings should be grouped together
      currentList = orderBy(bookingsList[listType], Booking_SortOrder, ['asc', 'asc']);
    } else {
      currentList = orderBy(affectedBookings, Booking_SortOrder, ['asc', 'asc']);
    }
    return currentList;
  }

  public getEmptyCategorisedBookingList(): ICategorisedBookingList {
    return {
      booked: [],
      waitlist: [],
      standbyList: [], // not used in Legacy Diary, as it is merged with WL
      standByListCount: [],
      waitListCount: [],
      seated: [],
      cancelled: [],
      finished: [],
    };
  }

  private getDefinitiveBookingValues(booking: IBooking): {
    isCancelled: boolean;
    isFinished: boolean;
    isSeated: boolean;
    isPartiallySeated: boolean;
    hasTable: boolean;
    isSBL: boolean;
    isWL: boolean;
    isWLOrSB: boolean;
  } {
    const isCancelled = booking.status && booking.status.statusType === bookingStatusType.cancelled;
    const isFinished = booking.status && booking.status.statusType === bookingStatusType.finished;
    const isSeated = booking.status && booking.status.statusType === bookingStatusType.seated;
    const isPartiallySeated =
      booking.status && booking.status.statusType === bookingStatusType.partiallySeated;
    const hasTable = booking.tables && booking.tables.length > 0;
    const isSBL = booking.bookingType === BookingType.StandbyList;
    const isWL = booking.bookingType === BookingType.WaitList;
    const isWLOrSB = isWL || isSBL;
    return {
      isCancelled,
      isFinished,
      isSeated,
      isPartiallySeated,
      hasTable,
      isSBL,
      isWL,
      isWLOrSB,
    };
  }

  public isBookingInBookingsColumn(booking: IBooking): boolean {
    const { isCancelled, isFinished, isSeated, hasTable, isSBL } =
      this.getDefinitiveBookingValues(booking);

    return booking.status && !isCancelled && !isFinished && hasTable && !isSeated && !isSBL;
  }

  public async getCategorisedBookingList(
    bookings: IBooking[],
    isStandbyListEnabled: boolean,
    schedule?: IDiarySchedule,
    standbyHasOwnColumn = false
  ): Promise<ICategorisedBookingList> {
    const bookingsList: ICategorisedBookingList = this.getEmptyCategorisedBookingList();
    bookings &&
      bookings.length > 0 &&
      [...bookings].reverse().forEach((booking: IBooking) => {
        const { isCancelled, isFinished, isSeated, isPartiallySeated, isWLOrSB, isSBL, isWL } =
          this.getDefinitiveBookingValues(booking);

        let isInList = false;
        if (this.isBookingInBookingsColumn(booking)) {
          bookingsList.booked.push(booking);
          isInList = true;
        }
        if (isCancelled) {
          bookingsList.cancelled.push(booking);
          isInList = true;
        }

        if (booking.status && !isCancelled) {
          if (standbyHasOwnColumn) {
            /**
             * In Diary Mobile WL and SBL have their own columns
             */
            if (isSBL) {
              bookingsList.standbyList.push(booking);
              isInList = true;
            } else if(isWL) {
              bookingsList.waitlist.push(booking);
              isInList = true;
            }
          } else if (isWLOrSB) {
            /**
             * In legacy Diary app, WL and SBL are in the same column
             */
            isStandbyListEnabled && booking.bookingType === BookingType.StandbyList
              ? bookingsList.standByListCount.push(booking)
              : bookingsList.waitListCount.push(booking);
            bookingsList.waitlist.push(booking);
            isInList = true;
          }
        }

        if (isFinished) {
          bookingsList.finished.push(booking);
          isInList = true;
        }
        if (booking.status && (isSeated || isPartiallySeated)) {
          bookingsList.seated.push(booking);
          isInList = true;
        }

        if (!isInList) {
          console.warn('Booking has no visibility in list', booking);
        }
      });

    const cloneDeepBookings = cloneDeep(bookings)
    const bookingsByTable = BookingAvailabilityService.getBookingsByTable(cloneDeepBookings as IBookingSubset[])
    // Add tableAvailable to booking
    if (bookingsList.waitlist.length && schedule) {
      const cloneWaitList = cloneDeep(bookingsList.waitlist)
      const waitListWithTableAvailable = await BookingAvailabilityService.checkWaitListForAvailableTables(schedule, cloneWaitList, bookingsByTable)
      bookingsList.waitlist = waitListWithTableAvailable || [];
    }
    if (bookingsList.standbyList?.length && schedule) {
      const cloneStandbyList = cloneDeep(bookingsList.standbyList)
      const standbyListWithTableAvailable = await BookingAvailabilityService.checkWaitListForAvailableTables(schedule, cloneStandbyList, bookingsByTable)
      bookingsList.standbyList = standbyListWithTableAvailable || [];

    }
    return bookingsList;
  }

  public getConflicts(affectedBookings: IAffectedBooking[]): {
    timeConflicts: IAffectedBooking[];
    tableConflicts: IAffectedBooking[];
  } {
    const timeBooking = affectedBookings.filter((ab) => ab.timeEffected);
    const tableBooking = affectedBookings.filter((ab) => !ab.timeEffected);

    const NESTED_SORT_ORDER = [`booking.${TIME}`, `booking.${LAST_NAME}`];
    const timeConflicts = orderBy(timeBooking, NESTED_SORT_ORDER, ['asc', 'asc']);
    const tableConflicts = orderBy(tableBooking, NESTED_SORT_ORDER, ['asc', 'asc']);
    return {
      timeConflicts,
      tableConflicts,
    };
  }

  updateCancelledBookingsInList(
    originalBookingsList: IBooking[],
    newBooking: IBooking,
    originalBooking: IBooking
  ): {
    listWasModified: boolean;
    updatedRunsheetBookings: IBooking[];
  } {
    let listWasModified = false;
    const updatedRunsheetBookings: IBooking[] = [...originalBookingsList];

    if (newBooking.status.statusType === bookingStatusType.cancelled) {
      listWasModified = true;
      this.removeCancelledBooking(newBooking._id, updatedRunsheetBookings);
    } else if (originalBooking?.status.statusType === bookingStatusType.cancelled) {
      listWasModified = true;
      this.insertUncancelledBooking(updatedRunsheetBookings, originalBookingsList, newBooking);
    }

    return {
      listWasModified,
      updatedRunsheetBookings,
    };
  }

  replaceBookingInList(list: IBooking[], newBooking: IBooking): boolean {
    const indexOfNewBooking = list.findIndex((b) => b._id === newBooking._id);
    if (indexOfNewBooking > -1) {
      list[indexOfNewBooking] = newBooking;
      return true;
    }
    return false;
  }

  private removeCancelledBooking(
    updatedBookingId: string,
    updatedRunsheetBookings: IBooking[]
  ): void {
    const bookingIndex = updatedRunsheetBookings.findIndex((b) => b._id === updatedBookingId);
    updatedRunsheetBookings.splice(bookingIndex, 1);
  }

  private insertUncancelledBooking(
    updatedRunsheetBookings: IBooking[],
    originalBookingsList: IBooking[],
    newBooking: IBooking
  ): void {
    // got to use the vm.bookings list to get the index because it doesn't exist in the vm.runsheetBookings anymore
    const bookingIndex = originalBookingsList.findIndex((b) => b._id === newBooking._id);
    if (bookingIndex > -1) {
      updatedRunsheetBookings.splice(bookingIndex, 1, newBooking);
    } else {
      updatedRunsheetBookings.unshift(newBooking);
    }
  }
}

const instance = new BookingListFilterService();
export default instance as BookingListFilterService;
